import { PlayerServiceAPI } from '@API/data-services/player-service'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { LoadingRows } from '@Components/util/loading-rows'
import { Flex, Skeleton, Table } from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { formatInTimeZone } from 'date-fns-tz'
import { useSelector } from 'react-redux'
import { RootState } from '@AppState/redux-store.ts'
import { LoyaltyRedemptionStatus } from '@Generated/back-office-client/src'
import { Pagination } from '@Components/common/pagination'
import { usePaginatedState } from '@Components/common/use-paginated-state'

const PAGE_SIZE = 25

export const SpinToWinHistory = () => {
  const params = useParams()
  const playerLookupId = params?.lookupId
  const [page, setPage] = usePaginatedState()

  const { activeCustomerMetadata } = useSelector((state: RootState) => state.users)

  const { data: spinHistory, isLoading: isHistoryLoading } = useQuery({
    queryKey: [`${playerLookupId}-spin-to-win-history-${page}`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return PlayerServiceAPI.getPlayerSpinHistory(
        playerLookupId!,
        page * PAGE_SIZE,
        (page + 1) * PAGE_SIZE,
      )
    },
    enabled: !!playerLookupId,
  })

  const mapSpinHistory = useCallback(() => {
    return spinHistory?.results.map((spin) => {
      const resolvedAtSegment = spin.resolvedAt ? (
        <>
          {formatInTimeZone(spin.resolvedAt, activeCustomerMetadata?.timezone ?? 'UTC', 'PP')}
          <br />
          {formatInTimeZone(spin.resolvedAt, activeCustomerMetadata?.timezone ?? 'UTC', 'p')}
        </>
      ) : null

      return (
        <Table.Row key={spin.id}>
          <Table.RowHeaderCell>
            {formatInTimeZone(spin.createdAt, activeCustomerMetadata?.timezone ?? 'UTC', 'PP')}
            <br />
            {formatInTimeZone(spin.createdAt, activeCustomerMetadata?.timezone ?? 'UTC', 'p')}
          </Table.RowHeaderCell>
          <Table.Cell>{spin.game.name}</Table.Cell>
          <Table.Cell>{spin.id}</Table.Cell>
          <Table.Cell>
            {spin.status === LoyaltyRedemptionStatus.ERROR ? 'Error - ' : ''}
            {spin.details}
          </Table.Cell>
          <Table.RowHeaderCell>{resolvedAtSegment}</Table.RowHeaderCell>
        </Table.Row>
      )
    })
  }, [activeCustomerMetadata?.timezone, spinHistory?.results])

  return (
    <Flex direction="column" gap="3">
      <Skeleton loading={isHistoryLoading}>
        {spinHistory?.results.length || isHistoryLoading ? (
          <Table.Root className="results-table">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Date & Time</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Game Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Transaction ID</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Details</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Resolution Date & Time</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>{isHistoryLoading ? <LoadingRows /> : mapSpinHistory()}</Table.Body>
          </Table.Root>
        ) : (
          <Flex>No spin history for {playerLookupId}</Flex>
        )}
      </Skeleton>
      {spinHistory?.totalResults ? (
        <Pagination
          totalItems={spinHistory.totalResults}
          pageSize={PAGE_SIZE}
          currentPage={page}
          onPageChange={setPage}
        />
      ) : null}
    </Flex>
  )
}
